import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Plus, Edit, Trash2, Building, Mail, Phone, MapPin, FileText, Search, LogOut, Settings, Zap, Globe } from 'lucide-react'
import { Button } from '../components/Button'
import Modal from '@/components/Modal'
import ErrorBanner from '@/components/ErrorBanner'
import { companyService, Company, AddCompanyData } from '@/services/companyService'
import { useNavigate } from 'react-router-dom'
import { authService } from '@/services/authService'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { responseErrorMessage, toUserMessage, logError } from '@/utils/errorMessage'
import deepWindLogo from '../assets/deepwind-logo.png'

const EMPTY_COMPANY: AddCompanyData = {
  name: '',
  contact_email: '',
  authorized_number: '',
  phone: '',
  address: ''
}

const CompanyManagementScreen: React.FC = () => {
  const navigate = useNavigate()
  const { language, setLanguage, t } = useLanguage()
  // Lokal bildirim yeniden-yazimi kaldirildi: global kuyruklu toast katmani kullaniliyor.
  const { showNotification } = useNotifications()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  /** Liste yuklenemedigindeki hata. Mevcut liste SILINMEZ, sadece banner eklenir. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  /** Ekleme/duzenleme modali icindeki hata (modal acikken sayfa banner'i gorunmez kalir). */
  const [formError, setFormError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  /** Silme onay modali icindeki hata. */
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [_currentUser, setCurrentUser] = useState<any>(null)
  const [newCompany, setNewCompany] = useState<AddCompanyData>({ ...EMPTY_COMPANY })

  /** Footer'daki buton form'un DISINDA kaldigi icin submit'i buradan tetikliyoruz
   *  (boylece e-posta alaninin native validasyonu da calisir). */
  const formRef = useRef<HTMLFormElement>(null)

  // Load current user and check admin access
  useEffect(() => {
    const loadCurrentUser = () => {
      try {
        const userData = localStorage.getItem('deepwind-user')
        if (userData) {
          const user = JSON.parse(userData)
          setCurrentUser(user)

          // Only superadmin can access company management
          if (user.role !== 'superadmin') {
            showNotification('error', 'Yetkisiz Erişim', 'Bu sayfaya erişim yetkiniz yok. Sadece SuperAdmin ortak yönetimi yapabilir.')
            setTimeout(() => navigate('/dashboard'), 2000)
          }
        }
      } catch (error) {
        logError('CompanyManagement.loadCurrentUser', error)
        showNotification('error', t.errors.title, 'Kullanıcı bilgileri yüklenemedi.')
      }
    }

    loadCurrentUser()
  }, [navigate])

  // Load companies
  const loadCompanies = async () => {
    try {
      setLoading(true)
      setLoadError(null)

      const response = await companyService.getCompanies()

      if (response.success && response.data) {
        setCompanies(response.data)
      } else {
        // Hata bos veri DEGILDIR: elde ne varsa korunur, kullaniciya "tekrar dene" sunulur.
        setLoadError(responseErrorMessage(response, t, t.errors.loadFailed))
      }
    } catch (error) {
      logError('CompanyManagement.loadCompanies', error)
      setLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      setLoading(false)
    }
  }

  const closeCompanyModal = () => {
    setShowAddModal(false)
    setEditingCompany(null)
    setFormError(null)
    setNewCompany({ ...EMPTY_COMPANY })
  }

  // Add company
  const handleAddCompany = async () => {
    if (!newCompany.name.trim()) {
      setFormError('Şirket adı gereklidir')
      return
    }

    // Client-side duplicate check
    const existingCompany = companies.find(
      company => company.name.toLowerCase().trim() === newCompany.name.toLowerCase().trim()
    )

    if (existingCompany) {
      setFormError(`"${newCompany.name}" adında bir şirket zaten mevcut`)
      return
    }

    try {
      setSaving(true)
      setFormError(null)

      const response = await companyService.addCompany(newCompany)

      if (response.success && response.data) {
        // Real-time update: Add new company to state
        setCompanies(prevCompanies => [...prevCompanies, response.data as Company])

        closeCompanyModal()
        showNotification('success', 'Başarılı', 'Şirket başarıyla eklendi')
      } else {
        // Backend mesaji (ornegin "Bu sirket adi zaten kullanilmaktadir: X") ezilmez.
        setFormError(responseErrorMessage(response, t, 'Şirket eklenirken hata oluştu'))
      }
    } catch (error) {
      logError('CompanyManagement.handleAddCompany', error)
      setFormError(toUserMessage(error, t, 'Şirket eklenirken hata oluştu'))
    } finally {
      setSaving(false)
    }
  }

  // Edit company
  const handleEditCompany = (company: Company) => {
    setEditingCompany(company)
    setFormError(null)
    setNewCompany({
      name: company.name,
      contact_email: company.contact_email || '',
      authorized_number: company.authorized_number || '',
      address: company.address || '',
      phone: company.phone || ''
    })
    setShowAddModal(true)
  }

  // Update company
  const handleUpdateCompany = async () => {
    if (!editingCompany || !newCompany.name.trim()) {
      setFormError('Şirket adı gereklidir')
      return
    }

    // Client-side duplicate check (exclude current company)
    const existingCompany = companies.find(
      company =>
        company.id !== editingCompany.id &&
        company.name.toLowerCase().trim() === newCompany.name.toLowerCase().trim()
    )

    if (existingCompany) {
      setFormError(`"${newCompany.name}" adında bir şirket zaten mevcut`)
      return
    }

    try {
      setSaving(true)
      setFormError(null)

      const response = await companyService.updateCompany(editingCompany.id, newCompany)

      if (response.success && response.data) {
        // Real-time update: Update the specific company in state
        setCompanies(prevCompanies =>
          prevCompanies.map(company =>
            company.id === editingCompany.id
              ? { ...company, ...response.data }
              : company
          )
        )

        closeCompanyModal()
        showNotification('success', 'Başarılı', 'Şirket başarıyla güncellendi')
      } else {
        setFormError(responseErrorMessage(response, t, 'Şirket güncellenirken hata oluştu'))
      }
    } catch (error) {
      logError('CompanyManagement.handleUpdateCompany', error)
      setFormError(toUserMessage(error, t, 'Şirket güncellenirken hata oluştu'))
    } finally {
      setSaving(false)
    }
  }

  const submitCompanyForm = () => {
    if (saving) return
    if (editingCompany) {
      void handleUpdateCompany()
    } else {
      void handleAddCompany()
    }
  }

  // Delete company
  const handleDeleteCompany = (company: Company) => {
    setDeletingCompany(company)
    setDeleteError(null)
    setShowDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setDeletingCompany(null)
    setDeleteError(null)
  }

  // Confirm delete
  const confirmDeleteCompany = async () => {
    if (!deletingCompany) return

    try {
      setDeleting(true)
      setDeleteError(null)

      const response = await companyService.deleteCompany(deletingCompany.id)

      if (response.success) {
        setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== deletingCompany.id))
        closeDeleteModal()
        showNotification('success', 'Başarılı', 'Şirket ve ilgili fotoğraflar başarıyla silindi')
        await loadCompanies() // Reload companies
      } else {
        // Yikici islem basarisiz: modal KAPANMAZ, sebep (ornegin "once turbinleri silin")
        // kullanicinin baktigi yuzeyde kalir.
        setDeleteError(responseErrorMessage(response, t, 'Şirket silinirken hata oluştu'))
      }
    } catch (error) {
      logError('CompanyManagement.confirmDeleteCompany', error)
      setDeleteError(toUserMessage(error, t, 'Şirket silinirken hata oluştu'))
    } finally {
      setDeleting(false)
    }
  }

  // Filter companies (exclude SuperAdmin company ID 999)
  const filteredCompanies = companies.filter(company =>
    company.id !== 999 && company.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Load companies on mount
  useEffect(() => {
    loadCompanies()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                  <img 
                    src={deepWindLogo} 
                    alt="VentraWind Logo" 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">VentraWind</h1>
                  <p className="text-xs text-gray-500">{t.dashboard.partnerManagement}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Zap className="w-4 h-4 mr-2" />
{t.dashboard.turbineManagement}
              </Button>
              <Button
                onClick={() => {}}
                className="bg-gray-400 cursor-not-allowed text-white"
                size="sm"
                disabled
              >
                <Building className="w-4 h-4 mr-2" />
{t.dashboard.partnerManagement}
              </Button>
              <Button
                onClick={() => navigate('/dashboard/users')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <Settings className="w-4 h-4 mr-2" />
{t.dashboard.userSettings}
              </Button>
              
              {/* Language Toggle Button */}
              <Button
                onClick={() => {
                  const newLanguage = language === 'tr' ? 'en' : 'tr'
                  setLanguage(newLanguage)
                }}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors min-w-[80px]"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'tr' ? 'English' : 'Türkçe'}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                onClick={async () => {
                  try {
                    await authService.logout()
                    window.location.href = '/'
                  } catch (error) {
                    // Bilerek sessiz: cikis her halukarda yerelde yapiliyor ve sayfa
                    // hemen terk ediliyor; gosterilecek bir bildirim kullaniciya ulasamaz.
                    logError('CompanyManagement.logout', error)
                    window.location.href = '/'
                  }
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">{t.dashboard.partnerManagement}</h2>
          <p className="text-gray-600">Şirket ortaklarını yönetin ve yeni ortaklar ekleyin</p>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Şirket ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Add Company Button */}
            <Button
              onClick={() => {
                setFormError(null)
                setShowAddModal(true)
              }}
              className="bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ortak Ekle
            </Button>
          </div>
        </div>

        {/* Liste yuklenemedi: mevcut liste korunur, tekrar deneme sunulur */}
        <ErrorBanner
          message={loadError}
          onRetry={() => { void loadCompanies() }}
          onDismiss={() => setLoadError(null)}
          className="mb-6"
        />

        {/* Companies Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t.loading.companiesLoading}</p>
          </div>
        ) : filteredCompanies.length === 0 ? (
          // Yukleme hatasi varsa "kayit yok" demek yaniltici olur: sadece hata banner'i kalir.
          loadError ? null : (
            <div className="text-center py-12">
              <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz ortak bulunmuyor'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm ? 'Farklı bir arama terimi deneyin' : 'İlk ortağınızı eklemek için butona tıklayın'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => {
                    setFormError(null)
                    setShowAddModal(true)
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  İlk Ortağı Ekle
                </Button>
              )}
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <motion.div
                key={company.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <Building className="w-8 h-8 text-blue-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                      <p className="text-sm text-gray-500">ID: {company.id}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditCompany(company)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Düzenle"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCompany(company)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{company.contact_email || 'Email belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{company.authorized_number || 'Yetki no belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{company.phone || 'Telefon belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5" />
                    <span>{company.address || 'Adres belirtilmemiş'}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Add / Edit Company Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={closeCompanyModal}
          title={editingCompany ? 'Ortak Düzenle' : 'Yeni Ortak Ekle'}
          error={formError}
          onDismissError={() => setFormError(null)}
          isSubmitting={saving}
          size="sm"
          footer={
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                variant="outline"
                onClick={closeCompanyModal}
                disabled={saving}
              >
                İptal
              </Button>
              <Button
                onClick={() => formRef.current?.requestSubmit()}
                disabled={saving || !newCompany.name.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {saving
                  ? (editingCompany ? 'Güncelleniyor...' : 'Ekleniyor...')
                  : (editingCompany ? 'Güncelle' : 'Ortak Ekle')
                }
              </Button>
            </div>
          }
        >
          <form
            ref={formRef}
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              submitCompanyForm()
            }}
          >
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Şirket Adı *
              </label>
              <input
                type="text"
                required
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Şirket adını girin"
              />
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İletişim E-postası
              </label>
              <input
                type="email"
                value={newCompany.contact_email}
                onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="ornek@sirket.com"
              />
            </div>

            {/* Authorized Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yetki Numarası
              </label>
              <input
                type="text"
                value={newCompany.authorized_number}
                onChange={(e) => setNewCompany({ ...newCompany, authorized_number: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Yetki numarasını girin"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon
              </label>
              <input
                type="tel"
                value={newCompany.phone}
                onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="0555 123 45 67"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adres
              </label>
              <textarea
                value={newCompany.address}
                onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                rows={3}
                disabled={saving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Şirket adresini girin"
              />
            </div>

            {/* Enter ile gonderim + native validasyon icin gizli submit */}
            <button type="submit" className="hidden" tabIndex={-1} aria-hidden="true" />
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal && !!deletingCompany}
          onClose={closeDeleteModal}
          title="Ortağı Sil"
          error={deleteError}
          onDismissError={() => setDeleteError(null)}
          isSubmitting={deleting}
          size="sm"
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                İptal
              </Button>
              <Button
                onClick={confirmDeleteCompany}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </Button>
            </div>
          }
        >
          <div className="flex items-start">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-gray-700">
                <strong>"{deletingCompany?.name}"</strong> adlı ortağı silmek istediğinizden emin misiniz?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Bu işlem geri alınamaz. Bu şirkete ait türbinler ve kullanıcılar varsa önce onları silmeniz gerekir.
              </p>
            </div>
          </div>
        </Modal>
      </main>
    </div>
  )
}

export default CompanyManagementScreen
